import { useRef, useEffect, useMemo } from "react";
import { Chart, registerables, ChartData, ChartOptions } from "chart.js";
import LegendChart from "../legend-chart/legend-chart";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(...registerables);

interface ChartLabel {
  label: string;
  color: string;
  type: string;
}

interface ChartLicenseProps {
  data: {
    [key: string]: {
      [key: string]: number;
    };
  };
  labels: ChartLabel[];
  reportInc?: boolean;
}

function ChartLicense({ data, labels, reportInc }: ChartLicenseProps) {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  const factoryLabels = useMemo(() => {
    return Object.keys(data)
      .map((factory) => ({
        factory,
        total: labels.reduce(
          (sum, { type }) => sum + (data[factory]?.[type] ?? 0),
          0
        ),
      }))
      .sort((a, b) => b.total - a.total) // от большего к меньшему
      .map((item) => item.factory);
  }, [data, labels]);

  const chartData: ChartData = useMemo(
    () => ({
      labels: factoryLabels,
      datasets: labels.map(({ label, color, type }) => ({
        label,
        data: factoryLabels.map((factory) => data[factory][type]),
        backgroundColor: color,
        borderRadius: 8,
      })),
    }),
    [data, labels]
  );

  const maxValue = useMemo(() => {
    const allValues = labels.flatMap(({ type }) =>
      factoryLabels.map((factory) => data[factory][type])
    );
    return Math.max(...allValues, 0);
  }, [data, labels]);

  const chartOptions: ChartOptions = useMemo(
    () => ({
      events: [],
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: (context: any) =>
            context.dataset.data[context.dataIndex] > 0,
          color: (context) => {
            const value = context.dataset.label;

            if (value === "Не начато") {
              return "#637381";
            } else {
              return "#FFFFFF";
            }
          },
          anchor: "center",
          align: "center",
          formatter: (value) => (value !== null ? `${value}шт` : null),
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            callback: (value) => `${value} шт`,
            stepSize: Math.ceil(maxValue / 10),
          },
        },
      },
    }),
    [maxValue]
  );

  const legendLabels = chartData.datasets.map((dataset) => ({
    text: dataset.label as string,
    color: dataset.backgroundColor as string,
  }));

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstanceRef.current) {
      const ctx = chartRef.current.getContext("2d");
      chartInstanceRef.current = new Chart(ctx as CanvasRenderingContext2D, {
        type: "bar",
        data: chartData,
        options: chartOptions,
        plugins: [ChartDataLabels],
      });
    } else {
      chartInstanceRef.current.data = chartData;
      chartInstanceRef.current.options = chartOptions;
      chartInstanceRef.current.update("none");
    }
  }, [chartData, chartOptions]);

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <canvas ref={chartRef} />
      <LegendChart labels={legendLabels} reportInc={reportInc} />
    </div>
  );
}

export default ChartLicense;
